import { ChangeDetectionStrategy, Component, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    LucideAngularModule,
    Package,
    Check,
    AlertCircle,
    RefreshCw,
    CheckCircle,
    AlertOctagon,
    Loader,
    X,
    Eye,
    Clock,
    Hash,
    Ban,
    Unlock,
    PenLine,
    Plus,
    Info,
    Filter,
    FilterX,
    Play
} from 'lucide-angular';
import { ProductSyncService, ProductSyncDetailResponse, ProductSyncBranchSummary } from '../../services/product-sync.service';
import { AuthService } from '../../services/auth.service';

type ReplicationActionType = 'Cadastro' | 'Alteração' | 'Bloqueio' | 'Desbloqueio';
type ReplicationStatus = 'Pendente' | 'Em Replicação' | 'Concluído' | 'Concluído com Erros' | 'Erro' | 'Cancelado';
type BranchStatus = 'Pendente' | 'Em Andamento' | 'Sucesso' | 'Erro' | 'Cancelado';

type GridColumn = 'actionType' | 'productCode' | 'productDescription' | 'fluigCode' | 'requestedAt' | 'requestedBy' | 'status';

interface ProductReplication {
    readonly uuid: string;
    readonly actionType: ReplicationActionType;
    readonly productCode: string;
    readonly productDescription: string;
    readonly fluigCode: string;
    readonly requestedAt: string;
    readonly requestedBy?: string;
    readonly status: ReplicationStatus;
    readonly branchSummary?: ProductSyncBranchSummary;
}

@Component({
    selector: 'app-product-replicator',
    templateUrl: './product-replicator.html',
    styleUrls: ['./product-replicator.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, LucideAngularModule],
})
export class ProductReplicatorComponent {
    readonly #productSyncService = inject(ProductSyncService);
    readonly authService = inject(AuthService);

    readonly icons = {
        Package, Check, AlertCircle, RefreshCw, CheckCircle, AlertOctagon,
        Loader, X, Eye, Clock, Hash, Ban, Unlock, PenLine, Plus, Info, Filter, FilterX, Play
    };

    readonly replications = signal<ProductReplication[]>([]);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);

    // Detail modal
    readonly selectedDetail = signal<ProductSyncDetailResponse | null>(null);
    readonly detailLoading = signal(false);
    readonly detailError = signal<string | null>(null);

    // Retry branch (bulk)
    readonly selectedBranchUuids = signal<Set<string>>(new Set());
    readonly pendingRetryUuids = signal<string[]>([]);
    readonly pendingRetryCodes = signal<string[]>([]);
    readonly retryErrorMessage = signal<string | null>(null);
    readonly retryingUuids = signal<Set<string>>(new Set());

    // --- GRID FILTERS ---
    readonly activeFilterColumn = signal<GridColumn | null>(null);
    readonly filterProductCode = signal('');
    readonly filterProductDescription = signal('');
    readonly filterFluigCode = signal('');
    readonly filterActionValues = signal<Set<string>>(new Set());
    readonly filterStatusValues = signal<Set<string>>(new Set());
    readonly filterDateStart = signal('');
    readonly filterDateEnd = signal('');
    readonly filterRequestedBy = signal('');

    readonly actionOptions: { label: string; value: ReplicationActionType }[] = [
        { label: 'Cadastro', value: 'Cadastro' },
        { label: 'Alteração', value: 'Alteração' },
        { label: 'Bloqueio', value: 'Bloqueio' },
        { label: 'Desbloqueio', value: 'Desbloqueio' },
    ];

    readonly statusOptions: { label: string; value: ReplicationStatus }[] = [
        { label: 'Pendente', value: 'Pendente' },
        { label: 'Em Replicação', value: 'Em Replicação' },
        { label: 'Concluído', value: 'Concluído' },
        { label: 'Concluído com Erros', value: 'Concluído com Erros' },
        { label: 'Erro', value: 'Erro' },
        { label: 'Cancelado', value: 'Cancelado' },
    ];

    // --- DETAIL MODAL FILTERS ---
    readonly detailBranchFilter = signal('');
    readonly detailStatusFilter = signal<Set<string>>(new Set());
    readonly activeDetailFilter = signal<'branchCode' | 'status' | null>(null);

    readonly detailBranchStatusOptions: { label: string; value: string }[] = [
        { label: 'Sucesso', value: 'success' },
        { label: 'Erro', value: 'error' },
        { label: 'Pendente', value: 'pending' },
        { label: 'Em Andamento', value: 'processing' },
        { label: 'Cancelado', value: 'cancelled' },
    ];

    readonly hasActiveFilters = computed(() =>
        this.filterActionValues().size > 0 ||
        this.filterStatusValues().size > 0 ||
        this.filterProductCode() !== '' ||
        this.filterProductDescription() !== '' ||
        this.filterFluigCode() !== '' ||
        this.filterDateStart() !== '' ||
        this.filterDateEnd() !== '' ||
        this.filterRequestedBy() !== ''
    );

    readonly filteredReplications = computed(() => {
        let data = [...this.replications()];
        const actionSet = this.filterActionValues();
        const statusSet = this.filterStatusValues();

        // Text filters (direct signals - no Record indirection)
        const codeTerm = this.filterProductCode().toLowerCase();
        if (codeTerm) {
            data = data.filter(r => (r.productCode || '').toLowerCase().includes(codeTerm));
        }

        const descTerm = this.filterProductDescription().toLowerCase();
        if (descTerm) {
            data = data.filter(r => (r.productDescription || '').toLowerCase().includes(descTerm));
        }

        const fluigTerm = this.filterFluigCode().toLowerCase();
        if (fluigTerm) {
            data = data.filter(r => (r.fluigCode || '').toLowerCase().includes(fluigTerm));
        }

        const requestedByTerm = this.filterRequestedBy().toLowerCase();
        if (requestedByTerm) {
            data = data.filter(r => (r.requestedBy || '').toLowerCase().includes(requestedByTerm));
        }

        // Date range filter
        const dateStart = this.filterDateStart();
        const dateEnd = this.filterDateEnd();
        if (dateStart) {
            const start = new Date(dateStart);
            start.setHours(0, 0, 0, 0);
            data = data.filter(r => new Date(r.requestedAt) >= start);
        }
        if (dateEnd) {
            const end = new Date(dateEnd);
            end.setHours(23, 59, 59, 999);
            data = data.filter(r => new Date(r.requestedAt) <= end);
        }

        // Checkbox filters
        if (actionSet.size > 0) {
            data = data.filter(r => actionSet.has(r.actionType));
        }
        if (statusSet.size > 0) {
            data = data.filter(r => statusSet.has(r.status));
        }

        // Sort by date desc
        return data.sort((a, b) =>
            new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime()
        );
    });

    readonly detailBranchCounts = computed(() => {
        const detail = this.selectedDetail();
        if (!detail?.branches) return { total: 0, success: 0, error: 0, pending: 0, processing: 0 };
        const branches = detail.branches;
        return {
            total: branches.length,
            success: branches.filter(b => b.syncStatus.toLowerCase() === 'success').length,
            error: branches.filter(b => b.syncStatus.toLowerCase() === 'error').length,
            pending: branches.filter(b => b.syncStatus.toLowerCase() === 'pending').length,
            processing: branches.filter(b => b.syncStatus.toLowerCase() === 'processing').length,
        };
    });

    readonly filteredDetailBranches = computed(() => {
        const detail = this.selectedDetail();
        if (!detail?.branches) return [];
        let branches = [...detail.branches];
        const branchTerm = this.detailBranchFilter().toLowerCase();
        const statusSet = this.detailStatusFilter();

        if (branchTerm) {
            branches = branches.filter(b => b.branchCode.toLowerCase().includes(branchTerm));
        }
        if (statusSet.size > 0) {
            branches = branches.filter(b => statusSet.has(b.syncStatus.toLowerCase()));
        }
        return branches;
    });

    readonly hasActiveDetailFilters = computed(() =>
        this.detailBranchFilter() !== '' || this.detailStatusFilter().size > 0
    );

    constructor() {
        this.loadData();
    }

    loadData(): void {
        this.loading.set(true);
        this.error.set(null);

        this.#productSyncService.getAll().subscribe({
            next: (data) => {
                this.replications.set(data.map(item => this.mapToReplication(item)));
                this.loading.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar replicações:', err);
                this.error.set('Falha ao carregar os dados. Tente novamente.');
                this.loading.set(false);
            }
        });
    }

    openDetails(item: ProductReplication): void {
        this.selectedDetail.set(null);
        this.detailLoading.set(true);
        this.detailError.set(null);
        this.detailBranchFilter.set('');
        this.detailStatusFilter.set(new Set());
        this.activeDetailFilter.set(null);
        this.selectedBranchUuids.set(new Set());

        this.#productSyncService.getById(item.uuid).subscribe({
            next: (detail) => {
                this.selectedDetail.set(detail);
                this.detailLoading.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar detalhes:', err);
                this.detailError.set('Falha ao carregar os detalhes.');
                this.detailLoading.set(false);
            }
        });
    }

    closeDetails(): void {
        this.selectedDetail.set(null);
        this.detailError.set(null);
        this.detailLoading.set(false);
    }

    get isModalOpen(): boolean {
        return this.detailLoading() || this.detailError() !== null || this.selectedDetail() !== null;
    }

    // --- GRID FILTER ACTIONS ---
    toggleFilterPopup(column: GridColumn, event: Event): void {
        event.stopPropagation();
        this.activeFilterColumn.set(this.activeFilterColumn() === column ? null : column);
    }

    closeFilterPopup(): void {
        this.activeFilterColumn.set(null);
    }

    clearFilter(column: GridColumn): void {
        if (column === 'productCode') this.filterProductCode.set('');
        if (column === 'productDescription') this.filterProductDescription.set('');
        if (column === 'fluigCode') this.filterFluigCode.set('');
        if (column === 'actionType') this.filterActionValues.set(new Set());
        if (column === 'status') this.filterStatusValues.set(new Set());
        if (column === 'requestedAt') {
            this.filterDateStart.set('');
            this.filterDateEnd.set('');
        }
        if (column === 'requestedBy') this.filterRequestedBy.set('');
        this.activeFilterColumn.set(null);
    }

    clearAllFilters(): void {
        this.filterProductCode.set('');
        this.filterProductDescription.set('');
        this.filterFluigCode.set('');
        this.filterActionValues.set(new Set());
        this.filterStatusValues.set(new Set());
        this.filterDateStart.set('');
        this.filterDateEnd.set('');
        this.filterRequestedBy.set('');
        this.activeFilterColumn.set(null);
    }

    toggleActionFilter(value: string): void {
        this.filterActionValues.update(s => {
            const n = new Set(s);
            n.has(value) ? n.delete(value) : n.add(value);
            return n;
        });
    }

    toggleStatusFilter(value: string): void {
        this.filterStatusValues.update(s => {
            const n = new Set(s);
            n.has(value) ? n.delete(value) : n.add(value);
            return n;
        });
    }

    selectAllActions(): void {
        this.filterActionValues.set(new Set(this.actionOptions.map(o => o.value)));
    }

    clearActionSelection(): void {
        this.filterActionValues.set(new Set());
    }

    selectAllStatuses(): void {
        this.filterStatusValues.set(new Set(this.statusOptions.map(o => o.value)));
    }

    clearStatusSelection(): void {
        this.filterStatusValues.set(new Set());
    }

    // --- DETAIL MODAL FILTER ACTIONS ---
    toggleDetailFilterPopup(column: 'branchCode' | 'status', event: Event): void {
        event.stopPropagation();
        this.activeDetailFilter.set(this.activeDetailFilter() === column ? null : column);
    }

    closeDetailFilterPopup(): void {
        this.activeDetailFilter.set(null);
    }

    toggleDetailStatusFilter(value: string): void {
        this.detailStatusFilter.update(s => {
            const n = new Set(s);
            n.has(value) ? n.delete(value) : n.add(value);
            return n;
        });
    }

    clearDetailBranchFilter(): void {
        this.detailBranchFilter.set('');
        this.activeDetailFilter.set(null);
    }

    clearDetailStatusFilter(): void {
        this.detailStatusFilter.set(new Set());
        this.activeDetailFilter.set(null);
    }

    clearAllDetailFilters(): void {
        this.detailBranchFilter.set('');
        this.detailStatusFilter.set(new Set());
        this.activeDetailFilter.set(null);
    }

    // --- RETRY BRANCH ACTIONS (BULK) ---

    /** Computed: error branches visible in the current filtered list */
    readonly errorBranchesInView = computed(() =>
        this.filteredDetailBranches().filter(b => b.syncStatus.toLowerCase() === 'error')
    );

    /** Computed: number of selected branch UUIDs */
    readonly selectedBranchCount = computed(() => this.selectedBranchUuids().size);

    toggleBranchSelection(uuid: string): void {
        this.selectedBranchUuids.update(s => {
            const n = new Set(s);
            n.has(uuid) ? n.delete(uuid) : n.add(uuid);
            return n;
        });
    }

    selectAllErrorBranches(): void {
        const errorUuids = this.errorBranchesInView().map(b => b.uuid);
        this.selectedBranchUuids.update(s => {
            const n = new Set(s);
            errorUuids.forEach(u => n.add(u));
            return n;
        });
    }

    clearBranchSelection(): void {
        this.selectedBranchUuids.set(new Set());
    }

    /** Open confirmation for a single branch retry */
    confirmRetryBranch(branchUuid: string, branchCode: string): void {
        this.pendingRetryUuids.set([branchUuid]);
        this.pendingRetryCodes.set([branchCode]);
    }

    /** Open confirmation for all currently selected branches */
    confirmRetrySelected(): void {
        const selected = this.selectedBranchUuids();
        const branches = this.filteredDetailBranches().filter(b => selected.has(b.uuid));
        this.pendingRetryUuids.set(branches.map(b => b.uuid));
        this.pendingRetryCodes.set(branches.map(b => b.branchCode));
    }

    cancelRetryBranch(): void {
        this.pendingRetryUuids.set([]);
        this.pendingRetryCodes.set([]);
    }

    executeRetryBranch(): void {
        const uuids = this.pendingRetryUuids();
        if (!uuids.length) return;

        this.pendingRetryUuids.set([]);
        this.pendingRetryCodes.set([]);
        this.retryingUuids.set(new Set(uuids));
        this.selectedBranchUuids.set(new Set());

        this.#productSyncService.retryBranchBulk(uuids).subscribe({
            next: () => {
                this.retryingUuids.set(new Set());
                // Reload the detail to reflect updated statuses
                const detail = this.selectedDetail();
                if (detail) {
                    this.#productSyncService.getById(detail.uuid).subscribe({
                        next: (updatedDetail) => {
                            this.selectedDetail.set(updatedDetail);
                        },
                        error: (err) => {
                            console.error('Erro ao recarregar detalhes:', err);
                        }
                    });
                }
            },
            error: (err: any) => {
                this.retryingUuids.set(new Set());
                const msg = err?.error?.message || err?.error?.title || err?.message || 'Erro desconhecido ao reprocessar filial(is).';
                this.retryErrorMessage.set(msg);
            }
        });
    }

    // --- MAPPING ---
    private mapToReplication(item: { uuid: string; fluigCode: string; productCode: string; description: string; syncStatus: string; operationType: string; createdAt: string; requestedBy?: string; branchSummary?: ProductSyncBranchSummary }): ProductReplication {
        return {
            uuid: item.uuid,
            actionType: this.mapOperationType(item.operationType),
            productCode: item.productCode,
            productDescription: item.description,
            fluigCode: item.fluigCode,
            requestedAt: item.createdAt,
            requestedBy: item.requestedBy,
            status: this.mapSyncStatus(item.syncStatus),
            branchSummary: item.branchSummary,
        };
    }

    mapOperationType(type: string): ReplicationActionType {
        switch (type.toLowerCase()) {
            case 'create': return 'Cadastro';
            case 'update': return 'Alteração';
            case 'block': return 'Bloqueio';
            case 'unblock': return 'Desbloqueio';
            default: return 'Cadastro';
        }
    }

    mapSyncStatus(status: string): ReplicationStatus {
        switch (status.toLowerCase()) {
            case 'pending': return 'Pendente';
            case 'processing': return 'Em Replicação';
            case 'success': return 'Concluído';
            case 'completed': return 'Concluído';
            case 'completedwitherrors': return 'Concluído com Erros';
            case 'error': return 'Erro';
            case 'cancelled': return 'Cancelado';
            default: return 'Pendente';
        }
    }

    mapBranchStatus(status: string): BranchStatus {
        switch (status.toLowerCase()) {
            case 'success': return 'Sucesso';
            case 'error': return 'Erro';
            case 'processing': return 'Em Andamento';
            case 'pending': return 'Pendente';
            case 'cancelled': return 'Cancelado';
            default: return 'Pendente';
        }
    }

    getActionIcon(actionType: ReplicationActionType) {
        switch (actionType) {
            case 'Cadastro': return this.icons.Plus;
            case 'Alteração': return this.icons.PenLine;
            case 'Bloqueio': return this.icons.Ban;
            case 'Desbloqueio': return this.icons.Unlock;
        }
    }

    getActionColor(actionType: ReplicationActionType): string {
        switch (actionType) {
            case 'Cadastro': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'Alteração': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'Bloqueio': return 'bg-red-50 text-red-700 border-red-200';
            case 'Desbloqueio': return 'bg-amber-50 text-amber-700 border-amber-200';
        }
    }

    getStatusColor(status: ReplicationStatus): string {
        switch (status) {
            case 'Pendente': return 'bg-slate-50 text-slate-600 border-slate-200';
            case 'Em Replicação': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'Concluído': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'Concluído com Erros': return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'Erro': return 'bg-red-50 text-red-700 border-red-200';
            case 'Cancelado': return 'bg-orange-50 text-orange-700 border-orange-200';
        }
    }

    getStatusIcon(status: ReplicationStatus) {
        switch (status) {
            case 'Pendente': return this.icons.Clock;
            case 'Em Replicação': return this.icons.Loader;
            case 'Concluído': return this.icons.CheckCircle;
            case 'Concluído com Erros': return this.icons.AlertCircle;
            case 'Erro': return this.icons.AlertOctagon;
            case 'Cancelado': return this.icons.X;
        }
    }

    getBranchStatusColor(status: BranchStatus): string {
        switch (status) {
            case 'Pendente': return 'bg-slate-50 text-slate-600 border-slate-200';
            case 'Em Andamento': return 'bg-blue-50 text-blue-700 border-blue-200';
            case 'Sucesso': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'Erro': return 'bg-red-50 text-red-700 border-red-200';
            case 'Cancelado': return 'bg-orange-50 text-orange-700 border-orange-200';
        }
    }

    getBranchStatusIcon(status: BranchStatus) {
        switch (status) {
            case 'Pendente': return this.icons.Clock;
            case 'Em Andamento': return this.icons.Loader;
            case 'Sucesso': return this.icons.Check;
            case 'Erro': return this.icons.AlertOctagon;
            case 'Cancelado': return this.icons.X;
        }
    }

    sanitizeErrorMessage(message: string | undefined): string {
        if (!message) return '';

        let text = message;

        // Fix double-encoded UTF-8 (Protheus sends mojibake)
        // Longer patterns first to avoid partial matches
        const replacements: [string, string][] = [
            ['ÃƒÂ¡', 'á'], ['ÃƒÂ£', 'ã'], ['ÃƒÂ§', 'ç'], ['ÃƒÂ©', 'é'],
            ['ÃƒÂ­', 'í'], ['ÃƒÂ³', 'ó'], ['ÃƒÂº', 'ú'], ['ÃƒÂ¢', 'â'],
            ['ÃƒÂª', 'ê'], ['ÃƒÂ´', 'ô'], ['ÃƒÂ¼', 'ü'], ['ÃƒÂ', 'à'],
            ['Ã¡', 'á'], ['Ã£', 'ã'], ['Ã§', 'ç'], ['Ã©', 'é'],
            ['Ã­', 'í'], ['Ã³', 'ó'], ['Ãº', 'ú'], ['Ã¢', 'â'],
            ['Ãª', 'ê'], ['Ã´', 'ô'], ['Ã¼', 'ü'], ['Ã ', 'à'],
            ['Ã‰', 'É'], ['Ã‡', 'Ç'], ['Ãƒ', 'Ã'], ['Â', ''],
        ];

        for (const [from, to] of replacements) {
            text = text.split(from).join(to);
        }

        // Clean up formatting
        text = text.replace(/\n{2,}/g, '\n').replace(/ {2,}/g, ' ').trim();

        return text;
    }

    getSuccessPercentage(summary?: ProductSyncBranchSummary): number {
        if (!summary || summary.total === 0) return 0;
        return Math.round((summary.success / summary.total) * 100);
    }

    getProgressBarColor(percentage: number): string {
        if (percentage === 100) return 'bg-emerald-500';
        if (percentage >= 70) return 'bg-blue-500';
        if (percentage >= 40) return 'bg-amber-500';
        return 'bg-red-500';
    }

    formatDate(isoString: string | null): string {
        if (!isoString) return '-';
        const d = new Date(isoString);
        if (isNaN(d.getTime())) return '-';
        const pad = (n: number) => n.toString().padStart(2, '0');
        return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }
}
